/**
 * Utility functions for converting between Model objects and different serialization formats
 */

import { Model, Variable, ObjectiveFunction, Constraint, ConstraintSense } from '../powerFlow/highs';

// Function to convert Model to LP format
export const modelToLP = (model: Model): string => {
  let lp = '';
  
  // Objective function
  lp += `${model.objective.sense}\n`;
  const objectiveTerms: string[] = [];
  
  for (const [variable, coefficient] of model.objective.coefficients) {
    if (coefficient === 1) {
      objectiveTerms.push(variable);
    } else if (coefficient === -1) {
      objectiveTerms.push(`- ${variable}`);
    } else if (coefficient > 0) {
      objectiveTerms.push(`${coefficient} ${variable}`);
    } else {
      objectiveTerms.push(`${coefficient} ${variable}`);
    }
  }
  
  lp += `  ${objectiveTerms.join(' + ').replace('+ -', '- ')}\n\n`;
  
  // Constraints
  if (model.constraints.length > 0) {
    lp += 'subject to\n';
    
    model.constraints.forEach(constraint => {
      const terms: string[] = [];
      
      for (const [variable, coefficient] of constraint.coefficients) {
        if (coefficient === 1) {
          terms.push(variable);
        } else if (coefficient === -1) {
          terms.push(`- ${variable}`);
        } else if (coefficient > 0) {
          terms.push(`${coefficient} ${variable}`);
        } else {
          terms.push(`${coefficient} ${variable}`);
        }
      }
      
      lp += `  ${constraint.name}: ${terms.join(' + ').replace('+ -', '- ')} ${constraint.sense} ${constraint.rhs}\n`;
    });
    
    lp += '\n';
  }
  
  // Variable bounds
  const boundsInfo = model.variables.filter(v => 
    v.lowerBound !== undefined || v.upperBound !== undefined || v.type !== 'continuous'
  );
  
  if (boundsInfo.length > 0) {
    lp += 'bounds\n';
    
    model.variables.forEach(variable => {
      if (variable.lowerBound !== undefined && variable.upperBound !== undefined) {
        lp += `  ${variable.lowerBound} <= ${variable.name} <= ${variable.upperBound}\n`;
      } else if (variable.lowerBound !== undefined) {
        lp += `  ${variable.name} >= ${variable.lowerBound}\n`;
      } else if (variable.upperBound !== undefined) {
        lp += `  ${variable.name} <= ${variable.upperBound}\n`;
      }
    });
    
    lp += '\n';
  }
  
  // Variable types
  const integerVars = model.variables.filter(v => v.type === 'integer').map(v => v.name);
  const binaryVars = model.variables.filter(v => v.type === 'binary').map(v => v.name);
  
  if (integerVars.length > 0) {
    lp += 'general\n';
    lp += `  ${integerVars.join(' ')}\n\n`;
  }
  
  if (binaryVars.length > 0) {
    lp += 'binary\n';
    lp += `  ${binaryVars.join(' ')}\n\n`;
  }
  
  lp += 'end';
  
  return lp;
};

// Function to convert Model to Graphviz DOT notation
export const modelToGraphviz = (model: Model): string => {
  interface GraphNode {
    id: string;
    type: 'variable' | 'objective';
    label: string;
  }

  interface GraphEdge {
    from: string;
    to: string;
    label?: string;
  }

  const nodes: GraphNode[] = [];
  const edges: GraphEdge[] = [];
  
  // Add variable nodes only (no objective node)
  model.variables.forEach(variable => {
    nodes.push({
      id: variable.name,
      type: 'variable',
      label: variable.name
    });
  });
  
  // Create edges between variables that share constraints
  model.constraints.forEach(constraint => {
    const variableNames = Array.from(constraint.coefficients.keys());
    
    // If constraint has 2 or more variables, connect them with edges labeled with constraint name
    if (variableNames.length >= 2) {
      // For each pair of variables in the constraint, create an edge
      for (let i = 0; i < variableNames.length; i++) {
        for (let j = i + 1; j < variableNames.length; j++) {
          edges.push({
            from: variableNames[i],
            to: variableNames[j],
            label: `${constraint.name}\\n(${constraint.sense})`
          });
        }
      }
    }
    
    // Skip single-variable constraints since we removed the objective node
  });
  
  // No objective edges since we removed the objective node
  
  // Generate DOT string
  let dot = 'graph OptimizationProblem {\n';
  dot += '  layout=neato;\n';
  dot += '  node [fontsize=10];\n';
  dot += '  edge [fontsize=8];\n';
  
  // Add nodes with styling (no labels, all variables)
  nodes.forEach(node => {
    dot += `  "${node.id}" [label="", fillcolor="lightblue", style=filled, shape=box];\n`;
  });
  
  // Add edges (no labels)
  edges.forEach(edge => {
    dot += `  "${edge.from}" -- "${edge.to}";\n`;
  });
  
  dot += '}\n';
  return dot;
};

